'use client';

import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { AddressSearch } from '@/components/shared/address-search';
import type { StructuredAddress } from '@/components/shared/address-search';
import { DEFAULT_COUNTRY } from '@/lib/constants/countries';
import {
  ADDRESS_VARIANT_TYPE_LABELS,
  type AddressVariant,
  type AddressVariantType,
  type CreateAddressVariantRequest
} from '@/lib/services/identity-variants';
import {
  ATTRIBUTE_COMPOSITE_PART,
  ATTRIBUTE_COMPOSITE_PARTS,
  ATTRIBUTE_COMPOSITE_PART_CONTROL,
  ATTRIBUTE_COMPOSITE_PART_LABEL,
  ATTRIBUTE_VALUE_TEXT,
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK
} from '@/components/attributes';
import { cn } from '@/lib/utils';
import { VariantInlineEditor } from './variant-inline-editor';

/**
 * Inline add / edit for one of the other addresses a person is or was at
 * (LF-5566 PROV-21; moved inline in LF-5714).
 *
 * This editor is currently the **only** way an address variant ever gets
 * created — extraction produces no multi-address signal, so every previous
 * address a share registry might match a holding against has to be captured
 * here.
 */

/** A new variant starts blank except for the default country (see NEW_STRUCTURED_ADDRESS). */
const NEW_ADDRESS: StructuredAddress = {
  streetAddress: '',
  suburb: '',
  state: '',
  postcode: '',
  country: DEFAULT_COUNTRY
};

function toStructured(variant?: AddressVariant): StructuredAddress {
  if (!variant) return NEW_ADDRESS;
  return {
    streetAddress: variant.value.streetAddress ?? '',
    suburb: variant.value.suburb ?? '',
    state: variant.value.state ?? '',
    postcode: variant.value.postcode ?? '',
    country: variant.value.country ?? ''
  };
}

interface AddressVariantFormProps {
  /** Absent when adding. */
  variant?: AddressVariant;
  onSubmit: (data: CreateAddressVariantRequest) => Promise<unknown>;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Passed through to the shell — see `VariantInlineEditor`'s own prop. */
  bare?: boolean;
  /** Commit-on-dismiss — see `VariantInlineEditor.autoSave`. */
  autoSave?: boolean;
  /** Reports the payload this form would submit, or `null` when not valid. */
  onDraftChange?: (payload: CreateAddressVariantRequest | null) => void;
}

export function AddressVariantForm({
  variant,
  onSubmit,
  onCancel,
  isSubmitting = false,
  bare,
  autoSave,
  onDraftChange
}: AddressVariantFormProps) {
  const isEdit = !!variant;

  const [address, setAddress] = useState<StructuredAddress>(() =>
    toStructured(variant)
  );
  const [type, setType] = useState<AddressVariantType>(
    variant?.type ?? 'residential'
  );
  const [isCurrent, setIsCurrent] = useState(variant?.isCurrent ?? true);
  const [periodNote, setPeriodNote] = useState(variant?.periodNote ?? '');

  /*
    **`country` does not count** (VALE-863). `AddressSearch.updateField` stamps
    `country: … || DEFAULT_COUNTRY` on *every* edit, so an address the reader
    typed into and then cleared still carries `Australia` — and counting it made
    an otherwise-empty draft look complete. On commit-on-dismiss that shipped an
    empty `residential` address the moment such a popover was dismissed; with
    the Save button it merely enabled a button that saved nothing useful.

    A country on its own is not an address in any case, which is why this is the
    right test rather than a workaround for the default.
  */
  const hasAddress = [
    address.streetAddress,
    address.suburb,
    address.state,
    address.postcode
  ].some((part) => !!part && part.trim().length > 0);

  /*
    Built once and shared by both paths, so commit-on-dismiss cannot diverge
    from the Save button's payload. `isPrimary: false` always — that flag marks
    the canonical address, which lives on the owner's own address field; a row
    added here is by definition not that, and the list filters primary rows out.
    The variant keeps the same postal-address shape as the base record, so the
    value passes straight through without transformation.
  */
  const payload: CreateAddressVariantRequest | null = useMemo(
    () =>
      hasAddress
        ? {
            value: {
              streetAddress: address.streetAddress || undefined,
              suburb: address.suburb || undefined,
              state: address.state || undefined,
              postcode: address.postcode || undefined,
              country: address.country || undefined
            },
            type,
            isPrimary: false,
            isCurrent,
            periodNote: periodNote.trim() || null
          }
        : null,
    [
      hasAddress,
      address.streetAddress,
      address.suburb,
      address.state,
      address.postcode,
      address.country,
      type,
      isCurrent,
      periodNote
    ]
  );

  /*
    Reported on every change rather than on blur: the host commits when the
    popover is dismissed, and a dismissal can come from a click anywhere on the
    page — there is no blur guaranteed to land first.
  */
  useEffect(() => {
    onDraftChange?.(payload);
  }, [payload, onDraftChange]);

  const handleSubmit = async () => {
    if (!payload) return;
    await onSubmit(payload);
    onCancel();
  };

  return (
    <VariantInlineEditor
      title={isEdit ? 'Edit address' : 'Add an address'}
      submitLabel={isEdit ? 'Save address' : 'Add address'}
      canSubmit={!!payload}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      bare={bare}
      autoSave={autoSave}
    >
      {/*
        The same chrome the Contacts drawer's address popover hands down
        (VALE-863) — this editor is a composite value too, so its five parts
        wear the treatment's geometry rather than `AddressSearch`'s page-form
        default. Without it the panel around them tightens and the parts inside
        do not, which reads worse than leaving both loose.
      */}
      <AddressSearch
        value={address}
        onChange={setAddress}
        partChrome={{
          parts: ATTRIBUTE_COMPOSITE_PARTS,
          part: ATTRIBUTE_COMPOSITE_PART,
          label: ATTRIBUTE_COMPOSITE_PART_LABEL,
          control: ATTRIBUTE_COMPOSITE_PART_CONTROL,
          suggestion: ATTRIBUTE_VALUE_TEXT,
          hint: ATTRIBUTE_COMPOSITE_PART_LABEL
        }}
      />

      <div className="grid gap-2 sm:grid-cols-2">
        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label
            htmlFor="variant-address-type"
            className={ATTRIBUTE_COMPOSITE_PART_LABEL}
          >
            Address type *
          </Label>
          <Select
            value={type}
            onValueChange={(next) => setType(next as AddressVariantType)}
          >
            <SelectTrigger
              id="variant-address-type"
              className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ADDRESS_VARIANT_TYPE_LABELS).map(
                ([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        <div className={ATTRIBUTE_COMPOSITE_PART}>
          <Label
            htmlFor="variant-address-period"
            className={ATTRIBUTE_COMPOSITE_PART_LABEL}
          >
            Period (optional)
          </Label>
          <Input
            id="variant-address-period"
            value={periodNote}
            onChange={(event) => setPeriodNote(event.target.value)}
            placeholder="e.g. until 2019"
            className={ATTRIBUTE_COMPOSITE_PART_CONTROL}
          />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="variant-address-current"
          checked={isCurrent}
          onCheckedChange={(checked) => setIsCurrent(checked === true)}
        />
        <Label
          htmlFor="variant-address-current"
          className={cn(ATTRIBUTE_COMPOSITE_PART_LABEL, 'font-normal')}
        >
          Still at this address
        </Label>
      </div>

      {/*
       * The "Use as the primary address" checkbox was removed in LF-5714. It
       * claimed the primary address is what court documents recite as "late of"
       * — untrue: that comes from the owner's own address field above. In fact
       * `isPrimary` on an address variant has no consumer anywhere, so the
       * control set a flag that did nothing while implying it changed the
       * recited address.
       */}
      <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
        Every address here is sent to the registry, which matches a holding on
        the registered name and address. The &ldquo;late of&rdquo; address
        recited in court documents is the one on the record above.
      </p>
    </VariantInlineEditor>
  );
}
